// Identidade do seller no admin (ADMIN-SELLER-ID): QUEM é, não só o id. Um componente só
// para toda tela do admin que mostra seller (lista e detalhe de sellers, transações,
// saques), em duas formas:
//   - `cell`: célula de tabela. Título, e-mail, documento e id; id e documento selecionáveis
//     com botão de copiar. O clique no id NÃO filtra: o filtro é o botão explícito
//     "Filtrar por este seller" (`onFilter`).
//   - `block`: linhas de chave/valor para o detalhe.
//
// Título = razão social / nome do titular (`legal_name`); sem ela, o `display_name`. O
// `display_name` (nome da loja) só aparece à parte quando difere do título.

import type { MouseEvent } from 'react';
import { Search } from '../../components/icons/index.tsx';
import { CopyId } from '../../components/kit/copy-id.tsx';
import { KVGroup } from '../../components/kit/kv.tsx';
import { RowOpenButton } from '../../components/kit/table.tsx';
import { Button } from '../../components/ui/button.tsx';
import { formatDocument } from '../admin-kyc/model.ts';

export interface SellerIdentityData {
  id: string;
  display_name: string;
  legal_name?: string | null | undefined;
  email?: string | null | undefined;
  document?: string | null | undefined;
}

/** Razão social ou nome do titular; cai no nome da loja enquanto o KYC não preencheu. */
export function sellerTitle(seller: SellerIdentityData): string {
  const legal = seller.legal_name?.trim();
  return legal === undefined || legal === '' ? seller.display_name : legal;
}

/** Nome da loja, só quando difere do título (senão seria a mesma coisa duas vezes). */
export function sellerStoreName(seller: SellerIdentityData): string | undefined {
  return seller.display_name === sellerTitle(seller) ? undefined : seller.display_name;
}

function stop(event: MouseEvent): void {
  // A linha da tabela abre o detalhe no clique; selecionar e copiar não pode abrir.
  event.stopPropagation();
}

function SellerCell({
  seller,
  onOpen,
  openLabel,
  onFilter,
}: {
  seller: SellerIdentityData;
  onOpen?: (() => void) | undefined;
  openLabel?: string | undefined;
  onFilter?: (() => void) | undefined;
}) {
  const title = sellerTitle(seller);
  const store = sellerStoreName(seller);
  const document = seller.document ?? '';
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: só barra a propagação do clique para a linha
    // biome-ignore lint/a11y/useKeyWithClickEvents: não é controle, os controles estão dentro
    <div className="min-w-0 leading-tight" onClick={stop}>
      <div className="flex min-w-0 items-center gap-1">
        {onOpen === undefined ? (
          <span className="truncate font-medium">{title}</span>
        ) : (
          <RowOpenButton label={openLabel ?? title} onOpen={onOpen}>
            <span className="block truncate font-medium">{title}</span>
          </RowOpenButton>
        )}
        {onFilter !== undefined && (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={`Filtrar por este seller: ${title}`}
            title="Filtrar por este seller"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={(event) => {
              event.stopPropagation();
              onFilter();
            }}
          >
            <Search aria-hidden="true" />
          </Button>
        )}
      </div>
      {store !== undefined && <div className="truncate text-11 text-muted">Loja: {store}</div>}
      {seller.email != null && seller.email !== '' && (
        <div className="truncate text-11 text-muted select-text">{seller.email}</div>
      )}
      {document !== '' && (
        <CopyId
          value={formatDocument(document)}
          label="Copiar documento"
          className="text-11 text-muted"
        />
      )}
      <div>
        <CopyId value={seller.id} label="Copiar id do seller" className="text-10 text-faint" />
      </div>
    </div>
  );
}

function dash() {
  return <span className="text-faint">-</span>;
}

function SellerBlock({
  seller,
  title,
}: {
  seller: SellerIdentityData;
  title?: string | undefined;
}) {
  const store = sellerStoreName(seller);
  const email = seller.email ?? '';
  const document = seller.document ?? '';
  return (
    <KVGroup
      {...(title === undefined ? {} : { title })}
      rows={[
        { k: 'Titular', v: sellerTitle(seller) },
        ...(store === undefined ? [] : [{ k: 'Loja', v: store }]),
        {
          k: 'E-mail',
          v: email === '' ? dash() : <CopyId value={email} label="Copiar e-mail" />,
        },
        {
          k: 'Documento',
          v:
            document === '' ? (
              dash()
            ) : (
              <CopyId value={formatDocument(document)} label="Copiar documento" />
            ),
        },
        { k: 'Id', v: <CopyId value={seller.id} label="Copiar id do seller" /> },
      ]}
    />
  );
}

export function SellerIdentity(
  props:
    | {
        variant: 'cell';
        seller: SellerIdentityData;
        onOpen?: (() => void) | undefined;
        /** Rótulo do botão de abrir ("Abrir <openLabel>"); default: o título. */
        openLabel?: string | undefined;
        onFilter?: (() => void) | undefined;
      }
    | { variant: 'block'; seller: SellerIdentityData; title?: string | undefined },
) {
  if (props.variant === 'cell') {
    return (
      <SellerCell
        seller={props.seller}
        onOpen={props.onOpen}
        openLabel={props.openLabel}
        onFilter={props.onFilter}
      />
    );
  }
  return <SellerBlock seller={props.seller} title={props.title} />;
}
